import { BRANDS, BRAND_INFO } from '@hub/shared'
import { Link } from 'react-router-dom'
import { BrandDot, brandFill } from '../components/Brand.js'
import { Icon } from '../components/Icon.js'
import { InstallHint } from '../components/InstallHint.js'
import { ItemCard } from '../components/ItemCard.js'
import { PageHeader } from '../components/PageHeader.js'
import { addDays, long, relative, today, weekStart } from '../lib/dates.js'
import { useSheetParam } from '../lib/filters.js'
import { useItems } from '../lib/items.js'
import type { Item } from '../lib/items.js'

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }): JSX.Element {
  return (
    <div className="card">
      <p className="num font-brand text-[2.25rem] font-light leading-none" style={tone && value > 0 ? { color: tone } : undefined}>
        {value}
      </p>
      <p className="mt-2 text-small text-label2/60">{label}</p>
    </div>
  )
}

/** What goes out when, what is stuck, what is next — at a glance. */
export function Overview(): JSX.Element {
  const t = today()
  const monday = weekStart(t)
  const horizon = addDays(monday, 13)
  const { data: soon = [] } = useItems({ from: addDays(t, -30), to: horizon })
  const { openItem, openNew } = useSheetParam()

  const live = (i: Item): boolean => i.status !== 'verworfen'
  const thisWeek = soon.filter((i) => live(i) && i.start >= monday && i.start <= addDays(monday, 6))
  const overdue = soon.filter((i) => live(i) && i.start < t && i.status !== 'veroeffentlicht')
  const upcoming = soon.filter((i) => live(i) && i.start >= t && i.status !== 'veroeffentlicht')
  const approval = soon.filter((i) => i.status === 'freigabe')
  const working = soon.filter((i) => i.status === 'in_arbeit').length

  // Group the next two weeks by day.
  const days = new Map<string, Item[]>()
  for (const item of upcoming) days.set(item.start, [...(days.get(item.start) ?? []), item])

  return (
    <div className="mx-auto max-w-6xl space-y-8 px-5 py-6 md:px-8">
      <PageHeader
        title="Übersicht"
        subtitle={long(t)}
        action={
          <div className="flex gap-2 md:hidden">
            <Link to="/verbindungen" className="glass-circle" aria-label="Verbindungen">
              <Icon name="claude" size={19} />
            </Link>
          </div>
        }
      />

      <InstallHint />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="gehen diese Woche raus" value={thisWeek.length} />
        <Stat label="warten auf Freigabe" value={approval.length} tone="#AF52DE" />
        <Stat label="überfällig" value={overdue.length} tone="#FF3B30" />
        <Stat label="in Arbeit" value={working} />
      </div>

      {/* Load per brand over the next two weeks */}
      <div className="card">
        <div className="flex items-baseline justify-between">
          <p className="text-small font-semibold">Nächste 14 Tage je Marke</p>
          <p className="num text-caption text-label2/60">{upcoming.length} geplant</p>
        </div>
        <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-fill/[0.12]">
          {BRANDS.map((b) => {
            const n = upcoming.filter((i) => i.brand === b).length
            return n > 0 ? (
              <div key={b} style={{ flex: n, backgroundColor: brandFill(b) }} className="transition-[flex] duration-500" />
            ) : null
          })}
        </div>
        <div className="mt-2.5 flex flex-wrap gap-4">
          {BRANDS.map((b) => (
            <span key={b} className="flex items-center gap-1.5 text-caption text-label2/70">
              <BrandDot brand={b} />
              {BRAND_INFO[b].name} <span className="num font-semibold text-label">{upcoming.filter((i) => i.brand === b).length}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
        <section className="space-y-5">
          {overdue.length > 0 && (
            <div>
              <p className="section-title px-0 text-danger">Überfällig</p>
              <div className="grid gap-2 xl:grid-cols-2">
                {overdue.map((i) => (
                  <ItemCard key={i.id} item={i} onOpen={() => openItem(i.id)} />
                ))}
              </div>
            </div>
          )}
          <p className="section-title px-0">Was als Nächstes rausgeht</p>
          {days.size === 0 && (
            <div className="card text-center">
              <p className="text-body">Für die nächsten zwei Wochen ist nichts geplant.</p>
              <button type="button" onClick={() => openNew()} className="btn-quiet mt-1">
                Ersten Eintrag anlegen
              </button>
            </div>
          )}
          {[...days.entries()].map(([day, list]) => (
            <div key={day} className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <div className="shrink-0 sm:w-20 sm:pt-3 sm:text-right">
                <p className={`text-small font-semibold ${day === t ? 'text-pebro' : 'text-label'}`}>{relative(day)}</p>
              </div>
              <div className="grid min-w-0 flex-1 gap-2 xl:grid-cols-2">
                {list.map((i) => (
                  <ItemCard key={i.id} item={i} onOpen={() => openItem(i.id)} />
                ))}
              </div>
            </div>
          ))}
        </section>

        <aside>
          <p className="section-title px-0">Wartet auf deine Freigabe</p>
          <div className="space-y-2">
            {approval.map((i) => (
              <ItemCard key={i.id} item={i} onOpen={() => openItem(i.id)} />
            ))}
            {approval.length === 0 && <p className="card text-center text-small text-label2/50">Nichts wartet auf dich.</p>}
          </div>
        </aside>
      </div>
    </div>
  )
}
